import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, ChangeEvent, useContext, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/SetPasswordFlowConfig";
import { getFreshStartBackPath } from "@/config/FreshStartOriginConfig";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { setPasswordService } from "@/services/continente-credentials/mobile/SetPasswordService";

import { FormError } from "@/models/continente-credentials/common/FormError"
import {
  verifySpecialChars,
  verifyLowerCase,
  verifyMinChar,
  verifyNumber,
  verifyUpperCase,
} from "../../utils/passwordRules";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsInput from "@/components/ui/Input";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import TermsAndConditionsCheckbox from "@/components/terms-and-conditions-checkbox/TermsAndConditionsCheckbox";

import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { SetPassword } from "@/models/continente-credentials/mobile/SetPassword";
import CheckBullet from "@/components/bullets/CheckBullet";
import { analyticsService } from "@/services/AnalyticsService";

const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface SharedSetPasswordPageProps {
  flowConfig: FlowConfig;
}

type Form = SetPassword & FormError;

export default function SetPasswordPage({ flowConfig }: SharedSetPasswordPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();
  const { handleResponse } = useApiResponseHandler();
  const digitalAssetContext = useContext(DigitalAssetContext);

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isAdminBlocked, setAdminBlocked] = useState(false);
  const [labelPasswordVisible, setLabelPasswordVisible] = useState(false);
  const [labelPasswordConfirmationVisible, setLabelPasswordConfirmationVisible] = useState(false);
  const [checkAcceptedTermsConditions, setAcceptedTermsConditions] = useState(false);
  const [isButtonEnabled, setButtonEnabled] = useState(false);
  const [isCheckBoxChecked, setCheckBoxChecked] = useState(false);

  const [minCharacters, setMinCharacters] = useState(false);
  const [containNumber, setContainNumber] = useState(false);
  const [containUppercase, setContainUppercase] = useState(false);
  const [containLowercase, setContainLowercase] = useState(false);
  const [containSpecialChar, setContainSpecialChar] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  const userData = location.state || {};
  const nextStep = userData.nextStep || "";

  const properties = userData.properties;
  const { mustAcceptTermsConditions } = properties || false;


  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = formState;

  const handleContactFormLinkClick = () => {
    history.push("/continente-credentials/faq" + ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION);
  };

  function verifyPasswordsMatch(pw: string, pwConfirmation: string): boolean {
    const match = pw === pwConfirmation && pw !== "";
    setPasswordsMatch(match);
    return match;
  }

  function checkAndEnableSubmit(
    pw: string,
    pwConfirmation: string,
    checkboxState: boolean | null = null
  ) {
    const checkboxChecked = checkboxState ?? isCheckBoxChecked

    const isValid =
      verifyPasswordsMatch(pw, pwConfirmation) &&
      verifyMinChar(pw) &&
      verifyNumber(pw) &&
      verifyLowerCase(pw) &&
      verifyUpperCase(pw) &&
      verifySpecialChars(pw) &&
      (mustAcceptTermsConditions ? checkboxChecked : true);

    setButtonEnabled(isValid);
  }

  const handlePasswordChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setPassword(value);
    setValue("password", value);
    setLabelPasswordVisible(!!value);
    checkAndEnableSubmit(value, passwordConfirmation);
  };

  const handlePasswordConfirmationChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setPasswordConfirmation(value);
    setValue("passwordConfirmation", value);
    setLabelPasswordConfirmationVisible(!!value);
    checkAndEnableSubmit(password, value);
  };

  const handleAcceptedTermsConditionsChange = () => {
    const newState = !checkAcceptedTermsConditions;
    setAcceptedTermsConditions(newState);
    setValue("acceptedTermsConditions", newState);
    setCheckBoxChecked(newState);
    checkAndEnableSubmit(password, passwordConfirmation, newState);
  };

  useEffect(() => {
    setMinCharacters(verifyMinChar(password));
    setContainNumber(verifyNumber(password));
    setContainUppercase(verifyUpperCase(password));
    setContainLowercase(verifyLowerCase(password));
    setContainSpecialChar(verifySpecialChars(password));
  }, [password]);

  useEffect(() => {
    register("password", {
      required: { value: true, message: t("common.required_field") },
    });

    if (mustAcceptTermsConditions) {
      register("acceptedTermsConditions", {
        required: { value: true, message: "" },
      });
    }
  }, [mustAcceptTermsConditions, register, t]);


  const onSubmit: SubmitHandler<SetPassword> = async (data) => {
    setLoading(true);
    analyticsService.trackSetPasswordSubmitted(flowConfig.flow);

    const { data: response, error } = await tryCatch(
      setPasswordService.setPassword(
        {
          password: data.password,
          passwordConfirmation: data.passwordConfirmation,
          acceptedTermsConditions: data.acceptedTermsConditions,
        },
        nextStep
      )
    );

    if (response?.kind === 'error') {
      const errorCode = response.code || 'set_password_failed';
      analyticsService.trackSetPasswordFailure(flowConfig.flow, errorCode);
    }

    handleResponse(
      response,
      {
        setError,
        setAdminBlocked,
        onSuccess: (response) => {
          analyticsService.trackSetPasswordSuccess(flowConfig.flow);
          history.push(response.value.nextStep.substring(4), response.value);
        },
      },
      error
    );

    setLoading(false);
  };

  const handleBackButton = (): number => {
    let backSteps = 1; // tem de ficar 1 senão não aparece no set password normal do fluxo
    if (location?.state?.previousStep) {
      backSteps = location?.state?.previousStep === "validate-otp" ? 2 : flowConfig.previousStepBackXTimes;
    }
    return backSteps;
  };

  return (
    <CredentialsForm
      id={`validate_password_form_${flowConfig.flow}`}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={t(flowConfig.labels.headerSubtitle ?? "")}
        showLoading={loading}
        previousStepBackXTimes={handleBackButton()}
        backClickToPushPath={getFreshStartBackPath(properties?.freshStartOrigin, digitalAssetContext.digitalAsset.clientId)}
      />
      <CredentialsInput
        id="password__input"
        autofocus
        labelText={t(flowConfig.labels.input)}
        isFilled={labelPasswordVisible}
        ariaLabel={t(flowConfig.labels.inputAria)}
        isError={!!(errors.password || errors.blocked || errors.submitError)}
        errorMsg={
          errors.password?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        onChange={handlePasswordChange}
        type="password"
      />
      <CredentialsInput
        id="password_confirmation__input"
        labelText={t(flowConfig.labels.inputRepeat)}
        ariaLabel={t(flowConfig.labels.inputRepeatAria)}
        isFilled={labelPasswordConfirmationVisible}
        isError={errors.passwordConfirmation ? true : false}
        errorMsg={
          errors.passwordConfirmation
            ? errors.passwordConfirmation.message
            : ""
        }
        onChange={handlePasswordConfirmationChange}
        type="password"
      />
      {errors.blocked && isAdminBlocked && (
        <div className="h-10 text-7xl font-bold text-center mb-30">
          <span
            onClick={handleContactFormLinkClick}
            className="text-black hover:underline cursor-pointer font-bold"
          >
            {t("common.contact_form")}
          </span>
        </div>
      )}
      <div className="my-0 relative flex justify-center" aria-hidden>
        <div className="flex w-94 m-auto flex-wrap flex-col">
          <div className="flex mb-3 font-bold text-[var(--on-surface-variant)]">
            {t("password_definition.password_validation_header")}
          </div>

          <CheckBullet
            checked={minCharacters}
            text={t(
              "password_definition.password_validation_length_maximum"
            )}
          />
          <CheckBullet
            checked={containNumber}
            text={t("password_definition.password_validation_number")}
          />
          <CheckBullet
            checked={containUppercase}
            text={t("password_definition.password_validation_uppercase")}
          />
          <CheckBullet
            checked={containLowercase}
            text={t("password_definition.password_validation_lowercase")}
          />
          <CheckBullet
            checked={containSpecialChar}
            text={t("password_definition.password_validation_special_char")}
          />
          <CheckBullet
            checked={passwordsMatch}
            text={t("password_definition.passwords_match")}
          />
        </div>
      </div>
      {mustAcceptTermsConditions && (
        <TermsAndConditionsCheckbox
          onClick={handleAcceptedTermsConditionsChange}
          isError={errors.acceptedTermsConditions ? true : false}
        />
      )}
      <Button
        disabled={loading || !isButtonEnabled || isSubmitting}
        aria-label={isButtonEnabled ? t(flowConfig.labels.nextButtonAria?.enabled ?? "") : t(flowConfig.labels.nextButtonAria?.disabled ?? "")}
      >
        {t(flowConfig.labels.nextButton)}
      </Button>
      <NeedHelpButton />
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter />
      </div>
    </CredentialsForm>
  );
}

export const createValidatePasswordPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SetPasswordPage flowConfig={config} />;
};

export const PasswordRecoverSetPassword = createValidatePasswordPage(AuthFlowType.RECOVER_PASSWORD);
export const PhoneRegistrationSetPassword = createValidatePasswordPage(AuthFlowType.PHONE_REGISTRATION);
export const EmailRegistrationSetPassword = createValidatePasswordPage(AuthFlowType.EMAIL_REGISTRATION);
export const SiteCredentialsSetPassword = createValidatePasswordPage(AuthFlowType.SITE_CREDENTIALS);
export const PhoneLoginEmailAssociationSetPassword = createValidatePasswordPage(AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION);
